import request from "util/chainedRequest";
import { action } from "mobx";
import extend from "lodash/extend";
import get from "lodash/get";
import isNull from "lodash/isNull";

// Usage:
// options.errorMessage (string) - the default handler will use this error message in the flash notification to the user
//                                 if no form error is provided by the server
// options.errorHandler (func)   - the default handler will surface the form error generated by the server, if one was provided. Otherwise,
//                                 the options.errorMessage will be shown. options.errorHandler should be a function which accept (err, res)
//                                 as parameters. If you supply options.errorHandler, any value supplied for options.errorMessage will be discarded.
// options.disableLoadSpinner (bool) - disables the loading spinner for the request

class ApiGetter {
    constructor(dispatcher, options) {
        this.dispatcher = dispatcher;
        this.settings = {
            errorMessage: "Oops! There was a problem getting data from the server",
            errorHandler: (err, res) => {
                const errorMessage = get(res, ["body", "errors", "form-level-error", 0, "errorMessageText"], this.errorMessage);
                this.dispatcher.emit("SHOW_FLASH_MESSAGE", {
                    message: errorMessage,
                    className: "warning"
                });
            }
        };

        this.settings = extend({}, this.settings, options);
    }

    createRequest() {
        let req = request(this.dispatcher);
        if (!this.settings.disableLoadSpinner) {
            req = req.withLoadSpinner();
        }
        return req;
    }

    fetch(endpoint, callback, errorHandler, queryParameters) {
        let req = this.createRequest();
        req.get(endpoint)
            .query(queryParameters)
            .respond()
            .onError(isNull(errorHandler) ? this.settings.errorHandler : errorHandler)
            .handle(action((err, res) => {
                callback(res.body.result);
            }));
    }

    fetchSearchResults(endpoint, callback, errorHandler) {
        let req = this.createRequest();
        req.get(endpoint)
            .respond()
            .onError(isNull(errorHandler) ? this.settings.errorHandler : errorHandler)
            .handle(action((err, res) => {
                callback(res);
            }));
    }

    del(endpoint, callback, errorHandler, pathParameters) {
        let req = this.createRequest();
        req.del(endpoint)
            .send(pathParameters)
            .respond()
            .onError(isNull(errorHandler) ? this.settings.errorHandler : errorHandler)
            .handle(action((err, res) => {
                callback(res.body.result);
            }));
    }

    post(endpoint, callback, errorHandler, pathParameters, queryParameters) {
        let req = this.createRequest();
        req.post(endpoint)
            .query(queryParameters)
            .send(pathParameters)
            .respond()
            .onRedirect()
            .onError(isNull(errorHandler) ? this.settings.errorHandler : errorHandler)
            .handle(action((err, res) => {
                callback(res.body.result);
            }));
    }

    postWithCredentials(endpoint, callback, errorHandler, pathParameters) {
        let req = this.createRequest();
        req.post(endpoint)
            .withCredentials()
            .send(pathParameters)
            .respond()
            .onError(isNull(errorHandler) ? this.settings.errorHandler : errorHandler)
            .onRedirect()
            .handle(action((err, res) => {
                callback(res.body.result);
            }));
    }

    // Do not mix postWithAttachment and send(...)
    // optional extraFields: plain object of name -> string | number | boolean (sent as multipart form fields)
    postWithAttachment(endpoint, callback, attachment, errorHandler, extraFields) {
        let req = this.createRequest();
        req.postWithAttachment(endpoint, attachment, undefined, extraFields)
            .respond()
            .onError(isNull(errorHandler) ? this.settings.errorHandler : errorHandler)
            .handle(action((err, res) => {
                callback(res.body.result);
            }));
    }
}

export default ApiGetter;
